import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../config/api';
import Navbar from '../components/navbar/Navbar';
import { formatDistanceToNow } from 'date-fns';
import Footer from '../components/footer/Footer';
import { motion } from 'framer-motion';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';

// Helper functions
const getShortDescription = (text, maxLength) => {
  if (!text) return '';
  const plainText = text.replace(/(<([^>]+)>)/gi, ''); // Remove HTML tags
  if (plainText.length <= maxLength) {
    return plainText;
  }
  return plainText.substring(0, maxLength) + '...';
};

const getShortTitle = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// BlogCard component
const BlogCard = ({ blog, index }) => {
  const navigate = useNavigate();
  const { _id, title, description, image, createdAt, slug } = blog;

  return (
    <motion.div
      className="col-md-6 col-lg-4 col-sm-12 d-flex"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <motion.div
        whileHover={{ scale: 1.02, boxShadow: "0 12px 24px rgba(0,0,0,0.2)" }}
        transition={{ type: "spring", stiffness: 300 }}
        style={{ backgroundColor: 'var(--bs-secondary)', color: 'var(--bs-accent)' }}
        className="property-card flex-grow-1 d-flex flex-column p-3 rounded"
      >
        <div
          className="property-image position-relative"
          onClick={() => navigate(`/blogs/${slug}`)}
          style={{ cursor: 'pointer' }}
        >
          <img
            src={image}
            className="img-fluid rounded"
            alt={title}
            loading="lazy"
          />
        </div>

        <div className="property-details flex-grow-1 d-flex flex-column justify-content-between">
          <div className="d-flex justify-content-between my-2">
            <h5 className="mb-0" style={{ color: 'var(--bs-primary)', fontWeight: '600' }}>
              {getShortTitle(title, 100)}
            </h5>
          </div>
          <p className="m-0" style={{ color: 'var(--bs-primary)' }}>
            {getShortDescription(description, 100)}
          </p>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <small style={{ color: 'gray' }}>
              {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
            </small>

            <span
              style={{ color: 'var(--bs-primary)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate(`/blogs/${slug}`)}
            >
              See More
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const SkeletonCard = () => (
  <div className="col-md-6 col-lg-4 col-sm-12 d-flex">
    <Box className="flex-grow-1 d-flex flex-column p-3 rounded" sx={{ backgroundColor: 'var(--bs-secondary)' }}>
      <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
      <Skeleton height={30} width="80%" sx={{ mt: 2 }} />
      <Skeleton height={20} width="90%" />
      <Skeleton height={20} width="60%" />
    </Box>
  </div>
);

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogs = async () => {
    try {
      const res = await ApiService.get('blogs/list');
      if (!res.data.error) {
        setBlogs(res.data.meta);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  return (
    <>
      <Navbar />
      <div style={{ backgroundColor: 'var(--bs-primary)', marginTop: '80px' }}>
        <div className="container-fluid p-3">
          <div className="row g-4">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            ) : blogs.length === 0 ? (
              <div className="col-12 text-center py-5" style={{ color: 'var(--bs-secondary)' }}>
                <h4>No Blogs Available</h4>
                <p>Currently, there are no blogs available. Please check back later.</p>
              </div>
            ) : (
              blogs.map((blog, index) => <BlogCard key={blog._id} blog={blog} index={index} />)
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Blogs;
